import { Controller } from "@hotwired/stimulus"

// PiP-style podcast player. The partial is mounted in BOTH
// layouts/app.html.erb (dashboard) and layouts/application.html.erb
// (public marketing pages) on an element with id="podcast-player"
// and data-turbo-permanent.
//
// Persistence strategy is layered:
//
//   1. PRIMARY — `data-turbo-permanent`: For Turbo Drive
//      navigations (the common case for in-app browsing), Turbo
//      preserves the live element by id. The audio element keeps
//      playing without interruption, no controller teardown, no
//      audible gap. This requires BOTH the source and destination
//      pages to render an element with the same id — which is why
//      the partial lives in both layouts. If only one layout
//      rendered it, cross-layout navigation (e.g. dashboard → public
//      page) would discard the live element and stop audio.
//
//   2. FALLBACK — localStorage: For full page reloads (no Turbo)
//      and the rare case where the destination page can't accept
//      the permanent element, the controller persists playback
//      state and the fresh element on the destination restores it
//      on `connect()`. Audio pauses for a few hundred ms during the
//      reload, then resumes from the saved timestamp.
//
// We persist `_userIntendsPlay` (what the user wanted) rather than
// `!audio.paused` (what the audio element happens to report) —
// because the `pause` event ALSO fires when the audio element is
// destroyed during navigation, and treating teardown as "user
// paused" would clobber the persisted intent and break auto-resume.
//
// Cross-element communication is via a window-level CustomEvent
// (`podcast:play`) — NOT a Stimulus action descriptor — because
// the player partial renders at the end of <body> (sibling to
// <main>) while card play buttons live inside <main>. Stimulus
// action descriptors only resolve their controller by walking
// ANCESTORS, so a `click->podcast-player#playEpisode` on a card
// button wouldn't reach this controller. The window event pattern
// matches the existing `app-upgrade-modal:open` convention used
// in shared/app/_top_app_bar.html.erb.
//
// v1 controls: play/pause + close. No scrubber, no skip buttons,
// no playback speed — see
// docs/superpowers/specs/2026-05-26-podcast-pip-player-design.md
// for the deferred scope.

const STORAGE_KEY = "podcast-player-state"
// Save currentTime to localStorage at most every N ms while
// playing — avoids hammering storage on `timeupdate` (which fires
// ~4×/sec).
const TIMEUPDATE_THROTTLE_MS = 2000

export default class extends Controller {
  static targets = ["audio", "title", "show", "thumbnail", "playIcon"]

  connect() {
    // Store bound references so we can remove these listeners in disconnect.
    // The host element is data-turbo-permanent, so the controller is
    // disconnected and reconnected across Turbo navigations on the SAME
    // <audio> element. Without removal here, listeners stack and each
    // play/pause/timeupdate fires N times after N navigations, causing
    // repeated close()/save side effects.
    this._onPlay        = this.onPlay.bind(this)
    this._onPause       = this.onPause.bind(this)
    this._onEnded       = this.close.bind(this)
    this._onErrorBound  = this.onError.bind(this)
    this._onTimeUpdate  = this.onTimeUpdate.bind(this)
    this.audioTarget.addEventListener("play",       this._onPlay)
    this.audioTarget.addEventListener("pause",      this._onPause)
    this.audioTarget.addEventListener("ended",      this._onEnded)
    this.audioTarget.addEventListener("error",      this._onErrorBound)
    this.audioTarget.addEventListener("timeupdate", this._onTimeUpdate)
    this._lastSavedAt = 0

    // User-intent flag — what the user WANTS the playback state to
    // be, independent of what the audio element's `.paused` happens
    // to be at any given moment. Critical: when the browser tears
    // down the audio element during navigation it fires `pause`,
    // which would otherwise corrupt our persisted state. We only
    // mutate this flag from explicit user actions (play, toggle,
    // close) — never from audio events.
    this._userIntendsPlay = false

    // Cross-element entry point for play requests.
    this._onPlayRequest = this.onPlayRequest.bind(this)
    window.addEventListener("podcast:play", this._onPlayRequest)

    // Save state right before navigation so the persisted snapshot
    // reflects user intent, not the system-fired pause that follows
    // when the audio element is destroyed during page swap. Both
    // events cover the two navigation paths (Turbo Drive + full
    // page reload).
    this._onBeforeNavigate = this.saveToStorage.bind(this)
    window.addEventListener("turbo:before-visit", this._onBeforeNavigate)
    window.addEventListener("beforeunload", this._onBeforeNavigate)

    // Restore saved playback state, if any. Runs on every page load
    // and Turbo navigation so the player feels continuous across
    // navigation even though the underlying element is fresh.
    this.restoreFromStorage()
  }

  disconnect() {
    window.removeEventListener("podcast:play", this._onPlayRequest)
    window.removeEventListener("turbo:before-visit", this._onBeforeNavigate)
    window.removeEventListener("beforeunload", this._onBeforeNavigate)
    if (this.hasAudioTarget) {
      this.audioTarget.removeEventListener("play",       this._onPlay)
      this.audioTarget.removeEventListener("pause",      this._onPause)
      this.audioTarget.removeEventListener("ended",      this._onEnded)
      this.audioTarget.removeEventListener("error",      this._onErrorBound)
      this.audioTarget.removeEventListener("timeupdate", this._onTimeUpdate)
    }
  }

  onPlayRequest(event) {
    this.playEpisode({ params: event.detail || {} })
  }

  playEpisode(event) {
    const { audioUrl, title, show, thumbnail } = event.params
    if (!audioUrl) return

    this.titleTarget.textContent = title || ""
    this.showTarget.textContent = show || ""
    if (thumbnail) {
      this.thumbnailTarget.src = thumbnail
      this.thumbnailTarget.style.display = ""
    } else {
      this.thumbnailTarget.style.display = "none"
    }

    if (this.audioTarget.src !== audioUrl) {
      this.audioTarget.src = audioUrl
    }
    this.element.classList.remove("hidden")
    this._userIntendsPlay = true
    this.audioTarget.play().catch(() => {
      // Autoplay policy can reject if the user gesture chain got
      // broken (shouldn't happen from a direct click but guard
      // anyway). Leave player visible so user can hit play again.
      this._userIntendsPlay = false
    })

    this.saveToStorage()
  }

  toggle() {
    if (this.audioTarget.paused) {
      this._userIntendsPlay = true
      this.audioTarget.play()
    } else {
      this._userIntendsPlay = false
      this.audioTarget.pause()
    }
    this.saveToStorage()
  }

  close() {
    this._userIntendsPlay = false
    this.audioTarget.pause()
    this.audioTarget.removeAttribute("src")
    this.audioTarget.load()
    this.element.classList.add("hidden")
    this.clearStorage()
  }

  // --- audio event handlers ---

  onPlay() {
    this.playIconTarget.textContent = "pause"
    this.saveToStorage()
  }

  onPause() {
    this.playIconTarget.textContent = "play_arrow"
    // Deliberately do NOT update `_userIntendsPlay` or call
    // `saveToStorage` here. The `pause` event ALSO fires when the
    // audio element is destroyed during page navigation; treating
    // teardown as "user paused" overwrites the persisted intent
    // with `wasPlaying: false`, which then prevents auto-resume on
    // the destination page. Intent transitions happen only in the
    // explicit user-action handlers (playEpisode, toggle, close).
    // The icon swap is fine to do here since it's harmless on
    // teardown.
  }

  onError() {
    this.showTarget.textContent = "Unable to play this episode"
    this.playIconTarget.textContent = "play_arrow"
  }

  onTimeUpdate() {
    // Throttle storage writes — `timeupdate` fires ~4 times per
    // second. Saving every 2s is enough resolution to resume
    // within ~1s of where the user left off.
    const now = Date.now()
    if (now - this._lastSavedAt < TIMEUPDATE_THROTTLE_MS) return
    this._lastSavedAt = now
    this.saveToStorage()
  }

  // --- localStorage state persistence ---

  saveToStorage() {
    try {
      // The dock is meant to follow the user ONLY while audio is
      // actively playing (or about to be). When intent flips to
      // false — user paused, hit close, or playback ended — we
      // clear the saved state instead of writing a "paused" record.
      // Without this, a paused podcast leaves stale state behind
      // that re-mounts the dock on every subsequent page render,
      // which users read as "why is this following me, I didn't
      // start anything."
      if (!this._userIntendsPlay) {
        localStorage.removeItem(STORAGE_KEY)
        return
      }
      const state = {
        audioUrl: this.audioTarget.src,
        title: this.titleTarget.textContent,
        show: this.showTarget.textContent,
        thumbnail: this.thumbnailTarget.src || "",
        currentTime: this.audioTarget.currentTime,
        // wasPlaying is always true at the write point now (the
        // intent gate above guarantees it). Kept in the payload for
        // forward-compatibility — older versions of this code wrote
        // `false` values that need to be tolerated on read.
        wasPlaying: true
      }
      if (!state.audioUrl) return
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch (e) {
      // localStorage blocked — non-fatal. Player still works for
      // the current page; just doesn't survive navigation.
    }
  }

  clearStorage() {
    try { localStorage.removeItem(STORAGE_KEY) } catch (e) {}
  }

  restoreFromStorage() {
    let state
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) state = JSON.parse(raw)
    } catch (_e) {
      // Corrupt/inaccessible storage — treat as no state.
    }

    // The ONLY case where the dock should be visible on connect is
    // a Turbo-permanent transfer of a still-playing audio element:
    // same DOM node carrying live playback from the previous page.
    // Anything else — a full page reload, a layout-change Turbo
    // couldn't bridge, opening a new tab — is a fresh page from
    // the user's perspective and they did NOT ask for audio to
    // resume. Auto-resuming from localStorage on those paths is
    // exactly the "why is the dock here when I didn't start
    // anything" surprise we keep failing to avoid.
    //
    // Detection: `audio.src` already matches the saved URL AND the
    // element has loaded data (`readyState > 0`). If those hold,
    // we know Turbo just handed us the same live `<audio>` node.
    const liveTransfer =
      state && state.audioUrl &&
      this.audioTarget.src === state.audioUrl &&
      this.audioTarget.readyState > 0

    if (liveTransfer) {
      // Sync surrounding UI to the saved metadata (title/show may
      // have been overwritten by a fresh partial render) but DO
      // NOT touch src/load/seek/play. The audio is mid-flight.
      this.titleTarget.textContent = state.title || ""
      this.showTarget.textContent = state.show || ""
      if (state.thumbnail) {
        this.thumbnailTarget.src = state.thumbnail
        this.thumbnailTarget.style.display = ""
      } else {
        this.thumbnailTarget.style.display = "none"
      }
      this._userIntendsPlay = !this.audioTarget.paused
      this.playIconTarget.textContent = this.audioTarget.paused ? "play_arrow" : "pause"

      // Even with a live transfer, if audio is paused the dock has
      // no reason to be on screen on a destination page. The user
      // can re-trigger from a podcast card. (Active playback case
      // is handled by leaving the existing visibility alone.)
      if (this.audioTarget.paused) {
        this.element.classList.add("hidden")
      }
      return
    }

    // Fresh page from the user's perspective. Drop any stale state
    // and ensure the dock is hidden. Don't auto-load, don't auto-
    // play, don't show a paused-with-saved-podcast dock — just
    // start from zero. New playback requires the user to click a
    // play button on a podcast card.
    this.clearStorage()
    this.element.classList.add("hidden")
  }
}
